import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, type ChatMessage, type ConversationSummary } from '../api';
import { Page, Card, Button, Badge, EmptyState, ChatTabs } from '../ui';

export function Conversations() {
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  function loadList() {
    if (!slug) return;
    api
      .listConversations(slug)
      .then(({ conversations }) => setItems(conversations))
      .catch((e) => setErr((e as { error?: string }).error === 'forbidden' ? 'Apenas maintainers/admins.' : 'Falha ao carregar'))
      .finally(() => setLoading(false));
  }

  function loadMessages(id: string) {
    if (!slug) return;
    api
      .getConversation(slug, id)
      .then(({ messages }) => setMessages(messages))
      .catch(() => setMessages([]));
  }

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (selected) loadMessages(selected);
    else setMessages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Auto-refresh da conversa aberta (e da lista) a cada 5s pra acompanhar ao vivo.
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => {
      loadList();
      if (selected) loadMessages(selected);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, selected, slug]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const selectedItem = useMemo(() => items.find((i) => i.id === selected) ?? null, [items, selected]);

  return (
    <Page
      title="Conversas"
      subtitle="Todas as conversas do projeto (web e Telegram) — o que cada agente recebeu, leu, escreveu e respondeu."
      actions={
        <label className="conv-auto">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> atualizar ao vivo
        </label>
      }
    >
      {slug && <ChatTabs slug={slug} />}
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : items.length === 0 ? (
        <EmptyState title="Nenhuma conversa ainda" description="Quando alguém falar com um agente (web ou Telegram), aparece aqui." />
      ) : (
        <div className="conv-layout">
          <aside className="conv-list">
            {items.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`conv-item ${c.id === selected ? 'is-active' : ''}`}
                onClick={() => setSelected(c.id)}
              >
                <div className="conv-item-top">
                  <span className={`conv-badge conv-${c.channel}`}>{c.channel === 'telegram' ? 'Telegram' : 'Web'}</span>
                  <span className="conv-item-agent">{c.agentName ?? (c.mode === 'router' ? 'Recepcionista' : '—')}</span>
                </div>
                <div className="conv-item-title">{c.title}</div>
                <div className="conv-item-meta">
                  {c.userName ?? 'usuário'} · {c.messageCount} msg · {formatRelative(c.updatedAt)}
                </div>
              </button>
            ))}
          </aside>

          <Card className="conv-thread">
            {!selected ? (
              <div className="center" style={{ color: 'var(--muted)' }}>Selecione uma conversa.</div>
            ) : (
              <>
                <div className="conv-thread-head">
                  <strong>{selectedItem?.title}</strong>
                  <span className="conv-thread-sub">
                    {selectedItem?.agentName ?? (selectedItem?.mode === 'router' ? 'Recepcionista' : '')} · {selectedItem?.userName}
                  </span>
                </div>
                <div className="conv-messages" ref={scrollRef}>
                  {messages.map((m) => (
                    <ConvMessage key={m.id} message={m} />
                  ))}
                  {messages.length === 0 && <div className="center" style={{ color: 'var(--muted)' }}>Sem mensagens.</div>}
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}

function ConvMessage({ message }: { message: ChatMessage }) {
  const tools = useMemo(() => {
    const raw = message.toolInfo?.['tools'];
    return Array.isArray(raw) ? (raw as Array<{ name: string; input?: unknown; ok?: boolean }>) : [];
  }, [message.toolInfo]);

  if (message.role === 'user') {
    return (
      <div className="conv-msg conv-msg-user">
        <div className="conv-msg-role">usuário</div>
        <div className="conv-msg-content">{message.content}</div>
      </div>
    );
  }
  return (
    <div className="conv-msg conv-msg-assistant">
      <div className="conv-msg-role">agente</div>
      {tools.length > 0 && (
        <div className="conv-activity">
          {tools.map((t, i) => (
            <span key={i} className="conv-activity-chip">
              {activityLabel(t.name, t.input)} {t.ok === false ? '⚠' : t.ok ? '✓' : ''}
            </span>
          ))}
        </div>
      )}
      <div className="conv-msg-content">{message.content || <em style={{ color: 'var(--muted)' }}>(sem texto)</em>}</div>
    </div>
  );
}

/** Traduz uma tool call do Claude em linguagem amigável ("o que ele fez"). */
function activityLabel(name: string, input?: unknown): string {
  const o = (input ?? {}) as Record<string, unknown>;
  const file = (o['file_path'] ?? o['path'] ?? o['notebook_path']) as string | undefined;
  const short = file ? file.split(/[\\/]/).pop() : undefined;
  switch (name) {
    case 'Read': return `📖 leu ${short ?? 'arquivo'}`;
    case 'Write': return `✍️ escreveu ${short ?? 'arquivo'}`;
    case 'Edit': return `✏️ editou ${short ?? 'arquivo'}`;
    case 'Glob': return `🔍 buscou ${(o['pattern'] as string) ?? 'arquivos'}`;
    case 'Grep': return `🔎 procurou "${(o['pattern'] as string) ?? ''}"`;
    default: return `⚙️ ${name}`;
  }
}

function formatRelative(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
